import { Switch } from "@mui/material";
import {
    MDBBadge,
    MDBBtn,
    MDBContainer,
    MDBIcon,
    MDBInputGroup,
    MDBRow,
    MDBTable,
    MDBTableBody,
} from "mdb-react-ui-kit";
import React, { useContext, useEffect, useState } from "react";
import "../../Pages/Accueil/Accueil.css";
import { useDarkMode } from "../../components/assets/DarkModeContext";
import ReferentialSelect from "../../components/assets/ReferentialSelect";
import ChipList from "../../components/chips/ChipList";
import {
    getAllUsersLight,
    getBusinessUnitsMetiers,
    getRefActivitySectors,
    getRefCompetences,
    getRefEducationLevels,
    getRefLanguageLevels,
    getRefLanguages,
    searchUser
} from "../../components/services/httpService";
import { TranslationContext } from "../../context/translations/translations-context";
import { affiliationCountriesSortedBy } from "../../utils/affiliationCountries";
import { generatePdfFileName } from "../../utils/generatePdfFileName";


export default function RechercheAvance() {
    const { displayLanguage, translations, changeLanguage } = useContext( TranslationContext );
    const pageTranslations = translations.advancedSearch;

    const [ users, setUsers ] = useState( [] );
    const { isDarkMode } = useDarkMode();
    const [ searchByCompetence, setSearchByCompetence ] = useState( '' );
    const [ showMoreCriteria, setShowMoreCriteria ] = useState( false );
    const [ chips, setChips ] = useState( [] );
    const [ managerId, setManagerId ] = useState( null );
    const [ managers, setManagers ] = useState( [] );
    const [ selectedRole, setSelectedRole ] = useState( '' );
    const [ minYears, setMinYears ] = useState( 0 );
    const [ maxYears, setMaxYears ] = useState( 99 );
    const [ minFormationLevel, setMinFormationLevel ] = useState( '' );
    const [ maxFormationLevel, setMaxFormationLevel ] = useState( '' );
    const [ allMetiers, setAllMetiers ] = useState( [] );
    const [ filteredMetiers, setFilteredMetiers ] = useState( [] );
    const [ selectedMetier, setSelectedMetier ] = useState( '' );

    const [ refActivitySectors, setRefActivitySectors ] = useState( [] );
    const [ refCompetences, setRefCompetences ] = useState( [] );
    const [ refEducationLevels, setRefEducationLevels ] = useState( [] );
    const [ refLanguageLevels, setRefLanguageLevels ] = useState( [] );
    const [ refLanguages, setRefLanguages ] = useState( [] );
    const [ affiliationCountries, setAffiliationCountries ] = useState( [] );

    const [ filteredBusinessSkills, setFilteredBusinessSkills ] = useState( [] );

    useEffect( () => {
        const sortMethod = ( a, b ) => {
            return a.translations[ displayLanguage ].localeCompare( b.translations[ displayLanguage ], displayLanguage );
        }
        const sortCompetences = ( a, b ) => {
            return a.competenceTranslations[ displayLanguage ]?.localeCompare( b.competenceTranslations[ displayLanguage ], displayLanguage );
        }
        const sortLanguageLevels = ( a, b ) => {
            return a.code?.localeCompare( b.code, displayLanguage );
        }
        const fetchActivitySectors = async () => {
            try {
                getRefActivitySectors()
                    .then( response => response.json() )
                    .then( data => setRefActivitySectors( data.sort( sortMethod ) ) );
            } catch ( error ) {
            }
        };

        const fetchEducationLevels = async () => {
            try {
                getRefEducationLevels()
                    .then( response => response.json() )
                    .then( data => setRefEducationLevels( data.sort( sortMethod ) ) );
            } catch ( error ) {
            }
        };

        const fetchCompetences = async () => {
            try {
                getRefCompetences()
                    .then( response => response.json() )
                    .then( data => setRefCompetences( data.sort( sortCompetences ) ) );
            } catch ( error ) {
            }
        };

        const fetchLanguages = async () => {
            try {
                getRefLanguages()
                    .then( response => response.json() )
                    .then( data => setRefLanguages( data.sort( sortMethod ) ) );
            } catch ( error ) {
            }
        }

        const fetchLanguageLevels = async () => {
            try {
                getRefLanguageLevels()
                    .then( response => response.json() )
                    .then( data => setRefLanguageLevels( data.sort( sortLanguageLevels ) ) );
            } catch ( error ) {
            }
        }
        const fetchUsers = async () => {
            try {
                getAllUsersLight()
                    .then( response => response.json() )
                    .then( data => {
                        setUsers( data );
                        const managerList = data.map( user => user.manager )
                            .filter( ( manager, index, self ) =>
                                manager !== null && // Verifica que el manager no sea null
                                self.findIndex( m => m !== null && m.id === manager.id ) === index
                            );
                        setManagers( managerList );
                    } );
            } catch ( error ) {
            }
        };

        const fetchAllMetiers = async () => {
            try {
                getBusinessUnitsMetiers()
                    .then( response => response.json() )
                    .then( data => {
                        setAllMetiers( data );
                        setFilteredMetiers( data );
                    } );
            } catch ( error ) {
                console.error( 'Failed to fetch metiers:', error );
            }
        };
        Promise.allSettled( [
            fetchUsers(),
            fetchActivitySectors(),
            fetchCompetences(),
            fetchEducationLevels(),
            fetchLanguageLevels(),
            fetchLanguages(),
            fetchAllMetiers() ]
        ).then( _ =>
            setAffiliationCountries( affiliationCountriesSortedBy( displayLanguage ) )
        );
    }, [] );

    const toggleDisplayLanguage = ( event ) => {
        const newLanguage = event.target.checked ? 'en' : 'fr';
        if ( newLanguage !== displayLanguage ) {
            changeLanguage( newLanguage );
            setAffiliationCountries( affiliationCountriesSortedBy( newLanguage ) );
            const sortMethod = ( a, b ) => {
                return a.translations[ newLanguage ].localeCompare( b.translations[ newLanguage ], newLanguage );
            }
            const sortCompetences = ( a, b ) => {
                return a.competenceTranslations[ newLanguage ]?.localeCompare( b.competenceTranslations[ newLanguage ], newLanguage );
            }
            setRefActivitySectors( prev => prev.sort( sortMethod ) );
            setRefCompetences( prev => prev.sort( sortCompetences ) );
            setRefEducationLevels( prev => prev.sort( sortMethod ) );
            setRefLanguages( prev => prev.sort( sortMethod ) );
        }
    };

    const handleMoreLessCriteriaButtonClick = () => {
        setShowMoreCriteria( !showMoreCriteria );
    };

    const handleSelectLanguage = ( e ) => {
        const selectedLanguageCode = e.target.value;
        const language = refLanguages.find( ( l ) => l.code === selectedLanguageCode );

        if ( language && !chips.some( chip => chip.code === language.code && chip.type === 'language' ) ) {
            setChips( [
                ...chips,
                {
                    type: 'language',
                    labels: language.translations,
                    code: language.code,
                    minLanguageLevel: '',
                    maxLanguageLevel: ''
                }
            ] );
        }
    };

    const handleBusinessSkillsInputChange = ( e ) => {
        const searchText = e.target.value.toLowerCase();
        setSearchByCompetence( searchText );

        const filtered = refCompetences.filter( comp =>
            comp.competenceTranslations?.[ displayLanguage ]?.toLowerCase().includes( searchText )
        );
        setFilteredBusinessSkills( filtered );
    };

    const handleMinFormationChange = ( e ) => {
        setMinFormationLevel( e.target.value );
    };

    const handleMaxFormationChange = ( e ) => {
        setMaxFormationLevel( e.target.value );
    };

    const handleActivitySectorChange = ( e ) => {
        const activitySectorId = +e.target.value;
        const activitySector = refActivitySectors.find( sector => sector.id === activitySectorId );
        if ( activitySector && !chips.some( chip => chip.id === activitySector.id && chip.type === 'sector' ) ) {
            setChips( [ ...chips, {
                type: 'sector',
                labels: activitySector.translations,
                id: activitySector.id
            } ] );
        }
    };

    const handleCountryChange = ( e ) => {
        const selectedCountryCode = e.target.value;
        const country = affiliationCountries.find( country => country.code === selectedCountryCode );
        if ( country && !chips.some( chip => chip.code === country.code && chip.type === 'country' ) ) {
            setChips( [
                ...chips,
                {
                    type: 'country',
                    labels: country.translations,
                    code: country.code,
                }
            ] );
        }
    };

    const handleRoleChange = ( e ) => {
        const selectedRole = e.target.value;
        if ( selectedRole && !chips.some( chip => chip.label === selectedRole && chip.type === 'role' ) ) {
            setChips( [ ...chips, { type: 'role', label: selectedRole } ] );
        }
    };

    const [ selectedIndex, setSelectedIndex ] = useState( -1 );

    const handleManagerChange = ( event ) => {
        const managerId = event.target.value;
        setManagerId( managerId );

        if ( managerId ) {
            const manager = managers.find( m => m.id === Number.parseInt( managerId ) );
            if ( manager ) {
                const newChip = { type: 'manager', label: `${ manager.prenom } ${ manager.nom }`, id: manager.id };
                setChips( chips => chips.filter( chip => chip.type !== 'manager' ).concat( newChip ) );
            }
        } else {
            setChips( chips => chips.filter( chip => chip.type !== 'manager' ) );
        }
    };

    const handleMetierKeyDown = ( e ) => {
        if ( e.key === 'ArrowDown' ) {
            setSelectedIndex( ( prevIndex ) => Math.min( prevIndex + 1, filteredMetiers.length - 1 ) );
            e.preventDefault();
        } else if ( e.key === 'ArrowUp' ) {
            setSelectedIndex( ( prevIndex ) => Math.max( prevIndex - 1, 0 ) );
            e.preventDefault();
        } else if ( e.key === 'Enter' ) {
            if ( selectedIndex >= 0 && selectedIndex < filteredMetiers.length ) {
                handleMetierClick( filteredMetiers[ selectedIndex ].metierName );
            }
            e.preventDefault();
        }
    };

    const handleBusinessSkillsInputKeyDown = ( e ) => {
        if ( e.key === 'ArrowDown' ) {
            setSelectedIndex( ( prevIndex ) => Math.min( prevIndex + 1, filteredBusinessSkills.length - 1 ) );
            e.preventDefault();
        } else if ( e.key === 'ArrowUp' ) {
            setSelectedIndex( ( prevIndex ) => Math.max( prevIndex - 1, 0 ) );
            e.preventDefault();
        } else if ( e.key === 'Enter' ) {
            if ( selectedIndex >= 0 && selectedIndex < filteredBusinessSkills.length ) {
                handleSelectBusinessSkill( filteredBusinessSkills[ selectedIndex ] );
            }
            e.preventDefault();
        }
    };

    const handleSelectBusinessSkill = ( businessSkill ) => {
        if ( businessSkill && !chips.some( chip => chip.id === businessSkill.id && chip.type === 'competence' ) ) {
            const newChip = {
                type: 'competence',
                labels: businessSkill.competenceTranslations,
                id: businessSkill.id,
                minYears: 0,
                maxYears: 99,
            };
            setChips( [ ...chips, newChip ] );
        }
        setSearchByCompetence( '' );
        setFilteredBusinessSkills( [] );
    };

    const clearSearch = () => {
        setChips( [] );
        search();
    };

    const filterDown = ( educationLevel ) => {
        return educationLevel.id <= ( maxFormationLevel || 10 );
    }

    const filterUp = ( educationLevel ) => {
        return educationLevel.id >= ( minFormationLevel || 0 );
    }


    const search = async () => {
        const searchData = {
            businessSkillCriteria: chips
                .filter( c => c.type === 'competence' )
                .map( c => {
                    return {
                        businessSkillId: c.id,
                        minimumExperienceInYears: c.minYears,
                        maximumExperienceInYears: c.maxYears,
                    };
                } ),

            languageSkillCriteria: chips
                .filter( c => c.type === 'language' )
                .map( c => {
                    return {
                        languageCode: c.code,
                        minimumLanguageLevelCode: c.minLanguageLevel === '' ? null : c.minLanguageLevel,
                        maximumLanguageLevelCode: c.maxLanguageLevel === '' ? null : c.maxLanguageLevel
                    };
                } ),

            activitySectorIds: chips
                .filter( c => c.type === 'sector' )
                .map( c => +c.id ),

            roles: chips
                .filter( c => c.type === 'role' )
                .map( c => c.label.toLowerCase() ),

            affiliationCountries: chips
                .filter( c => c.type === 'country' )
                .map( c => c.code ),

            metier: chips
                .filter( c => c.type === 'metier' )
                .map( c => c.label.toLowerCase() ),

            formationLevel: {
                min: +minFormationLevel,
                max: +maxFormationLevel,
            },

            experienceLevel: {
                min: minYears,
                max: maxYears,
            },

            managerId: managerId,
        };


        searchUser( searchData )
            .then( response => response.json() )
            .then( data => {
                setUsers( data );
            } )
            .catch( err => {
                console.log( 'An error occurred', err );
            } );
    }

    //Dropdown
    const [ dropdownStates, setDropdownStates ] = useState( {} );
    const [ selectedResumes, setSelectedResumes ] = useState( [] );
    const [ selectedUser, setSelectedUser ] = useState( null );

    const handleUserClick = ( user ) => {
        const isCurrent = selectedUser && selectedUser.id === user.id;
        const newState = !dropdownStates[ user.id ];

        setDropdownStates( prev => ( {
            ...prev,
            [ user.id ]: !isCurrent
        } ) );

        if ( !isCurrent ) {
            setSelectedUser( user );
            setSelectedResumes( user.resumes );
        } else {
            setSelectedUser( null );
            setSelectedResumes( [] );
        }
    };

    useEffect( () => {
        if ( selectedMetier ) {
            setFilteredMetiers( allMetiers.filter( metier =>
                metier.metierName.toLowerCase().includes( selectedMetier.toLowerCase() )
            ) );
        } else {
            setFilteredMetiers( allMetiers );
        }
    }, [ selectedMetier, allMetiers ] );

    const addMetierChip = ( metierName ) => {
        if ( !chips.some( chip => chip.label === metierName && chip.type === 'metier' ) ) {
            setChips( [ ...chips, { type: 'metier', label: metierName } ] );
        }
    };

    const handleMetierClick = ( metierName ) => {
        setSelectedMetier( '' );
        addMetierChip( metierName );
    };
    const textBeforeIcon = ( <MDBIcon fas icon='search' className={ isDarkMode ? "text-white" : "text-muted" }/> );
    const textBeforeMin = ( <span className={ isDarkMode ? "text-white" : "text-muted" }>Min</span> );
    const textBeforeMax = ( <span className={ isDarkMode ? "text-white" : "text-muted" }>Max</span> );

    return (
        <MDBContainer fluid className={ isDarkMode ? 'bg-dark-mode' : 'bg-light' }>
            <MDBContainer className="animation-fade-in pb-5">
                <div
                    className="p-5 bg-image"
                    style={ {
                        height: "300px",
                        backgroundImage: 'url("./bg/recherche-avance.jpg")',
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center bottom",
                        backgroundAttachment: "fixed",
                    } }
                ></div>
                <MDBRow className="text-center pt-3">
                    <div className={ isDarkMode ? "text-white" : "text-muted" }>
                        <h1 className="py-3">{ pageTranslations.search }</h1>
                    </div>
                    <div className="d-flex align-items-center me-4 justify-content-center">
                        <i className="flag flag-france"></i>
                        <Switch
                            checked={ displayLanguage === 'en' }
                            onChange={ toggleDisplayLanguage }
                            name="languageSwitch"
                            inputProps={ { 'aria-label': 'language switch' } }
                        />
                        <i className="flag flag-united-kingdom"></i>
                    </div>
                </MDBRow>
                <div style={ { display: 'flex', justifyContent: 'space-between' } }>

                    <div className="competence-suggestions-container-recherche">
                        <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.metier }</p>
                        <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeIcon }>
                            <input
                                className={ `form-control ${ isDarkMode ? "text-white" : "text-muted" }` }
                                style={ { backgroundColor: 'transparent' } }
                                type="text"
                                placeholder={ pageTranslations.searchBy }
                                value={ selectedMetier }
                                onChange={ ( e ) => setSelectedMetier( e.target.value ) }
                                onKeyDown={ handleMetierKeyDown }
                            />
                            { selectedMetier && (
                                <div className="suggestions-recherche">
                                    { filteredMetiers.map( ( metier, index ) => (
                                        <div key={ metier.metierId }
                                             className={ `suggestion-item-recherche ${ index === selectedIndex ? 'selected' : '' }` }
                                             onClick={ () => handleMetierClick( metier.metierName ) }>
                                            { metier.metierName }
                                        </div>
                                    ) ) }
                                </div>
                            ) }
                        </MDBInputGroup>
                    </div>
                    <div className="competence-suggestions-container-recherche">
                        <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.businessSkills }</p>
                        <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeIcon }>
                            <input className={ `form-control ${ isDarkMode ? "text-white" : "text-muted" }` }
                                   style={ { backgroundColor: 'transparent' } }
                                   type='text'
                                   placeholder={ pageTranslations.searchBy }
                                   value={ searchByCompetence }
                                   onChange={ handleBusinessSkillsInputChange }
                                   onKeyDown={ handleBusinessSkillsInputKeyDown }
                            />
                            { searchByCompetence && (
                                <div className="suggestions-recherche">
                                    { filteredBusinessSkills.map( ( businessSkill, index ) => (
                                        <div key={ index }
                                             className={ `suggestion-item-recherche ${ index === selectedIndex ? 'selected' : '' }` }
                                             onClick={ () => handleSelectBusinessSkill( businessSkill ) }>
                                            { businessSkill.competenceTranslations?.[ displayLanguage ] }
                                        </div>
                                    ) ) }
                                </div>
                            ) }
                        </MDBInputGroup>
                    </div>
                    <div>
                        <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.languages }</p>
                        <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeIcon }>
                            <ReferentialSelect
                                name={ "language-select" }
                                elementId={ "language-select" }
                                firstOption={ pageTranslations.selectALanguage }
                                referential={ refLanguages }
                                displayLanguage={ displayLanguage }
                                className={ isDarkMode ? "text-white" : "text-muted" }
                                handleChange={ handleSelectLanguage }
                            />
                        </MDBInputGroup>
                    </div>
                    <div data-role="main">
                        <div>
                            <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.levelExp }</p>
                            <div className="d-flex">
                                <div className="">
                                    <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeMin }>
                                        <input type="number" style={ { width: "50px" } } value={ minYears }
                                               onChange={ ( e ) => setMinYears( Number( e.target.value ) ) } min="0"
                                               max="99"/>
                                    </MDBInputGroup>
                                </div>
                                <div className="">
                                    <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeMax }>
                                        <input type="number" style={ { width: "50px" } } value={ maxYears }
                                               onChange={ ( e ) => setMaxYears( Number( e.target.value ) ) } min="0"
                                               max="99"/>
                                    </MDBInputGroup>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    { showMoreCriteria && (
                        <div className="d-flex justify-content-center gap-5">
                            <div>
                                <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.levelForm }</p>
                                <div className="d-flex gap-2">
                                    <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeMin }>
                                        <ReferentialSelect
                                            name={ "min-education-level" }
                                            elementId={ "min-education-level" }
                                            firstOption={ pageTranslations.filterBy }
                                            handleChange={ handleMinFormationChange }
                                            referential={ refEducationLevels.filter( filterDown ) }
                                            selectedItem={ minFormationLevel }
                                            displayLanguage={ displayLanguage }
                                            className={ `form-control ${ isDarkMode ? "text-white" : "text-muted" }` }
                                        />
                                    </MDBInputGroup>
                                    <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeMax }>
                                        <ReferentialSelect
                                            elementId={ "max-education-level" }
                                            name={ "max-education-level" }
                                            firstOption={ pageTranslations.filterBy }
                                            handleChange={ handleMaxFormationChange }
                                            referential={ refEducationLevels.filter( filterUp ) }
                                            selectedItem={ maxFormationLevel }
                                            displayLanguage={ displayLanguage }
                                            className={ `form-control ${ isDarkMode ? "text-white" : "text-muted" }` }
                                        />
                                    </MDBInputGroup>
                                </div>
                            </div>
                            <div>
                                <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.sector }</p>
                                <MDBInputGroup className='mb-5' noBorder textBefore={ textBeforeIcon }>
                                    <ReferentialSelect
                                        elementId={ "activity-sectors" }
                                        name={ "activity-sectors" }
                                        className={ `form-control ${ isDarkMode ? "text-white" : "text-muted" }` }
                                        style={ { backgroundColor: 'transparent' } } type='text'
                                        firstOption={ pageTranslations.searchBy }
                                        displayLanguage={ displayLanguage }
                                        handleChange={ handleActivitySectorChange }
                                        selectedItem={ '' }
                                        referential={ refActivitySectors }
                                    />
                                </MDBInputGroup>
                            </div>
                            <div>
                                <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.countryCo }</p>
                                <MDBInputGroup className='mb-5' noBorder textBefore={ textBeforeIcon }>
                                    <ReferentialSelect
                                        elementId={ "rattachement" }
                                        name={ "rattachement" }
                                        handleChange={ handleCountryChange }
                                        selectedItem={ '' }
                                        displayLanguage={ displayLanguage }
                                        style={ { backgroundColor: 'transparent' } }
                                        firstOption={ pageTranslations.searchBy }
                                        referential={ affiliationCountries }
                                    />
                                </MDBInputGroup>
                            </div>
                            <div>
                                <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>Manager</p>
                                <MDBInputGroup className='mb-5' noBorder textBefore={ textBeforeIcon }>
                                    <select
                                        value={ managerId || '' }
                                        onChange={ handleManagerChange }
                                        className={ `form-control ${ isDarkMode ? "text-white" : "text-muted" }` }
                                        style={ { backgroundColor: 'transparent' } }
                                    >
                                        <option value="">{ pageTranslations.searchBy }</option>
                                        { managers.map( ( manager, index ) => (
                                            <option key={ index } value={ manager.id }>
                                                { manager.prenom } { manager.nom }
                                            </option>
                                        ) ) }
                                    </select>
                                </MDBInputGroup>
                            </div>
                            <div>
                                <p className={ `text-center ${ isDarkMode ? "text-white" : "text-muted" }` }>{ pageTranslations.role }</p>
                                <MDBInputGroup className='mb-3' noBorder textBefore={ textBeforeIcon }>
                                    <select
                                        className={ `form-control ${ isDarkMode ? "text-white" : "text-muted" }` }
                                        style={ { backgroundColor: 'transparent' } }
                                        aria-label="Sélectionnez la tribe"
                                        value={ selectedRole }
                                        onChange={ handleRoleChange }
                                    >
                                        <option value="">{ pageTranslations.searchBy }</option>
                                        <option value="CANDIDAT">Candidat</option>
                                        <option value="CONSULTANT">Consultant</option>
                                        <option value="MANAGER">Manager</option>
                                        <option value="SUPERADMIN">Super Admin</option>
                                    </select>
                                </MDBInputGroup>
                            </div>
                        </div>
                    ) }
                    <div className="mb-3 text-center">
                        <MDBBtn onClick={ handleMoreLessCriteriaButtonClick } className="btn_primary">
                            <MDBIcon fas icon="angle-down" className="me-2" style={ {
                                transition: "transform 0.2s",
                                transform: showMoreCriteria ? "rotate(180deg)" : "rotate(0deg)"
                            } }/>
                            { showMoreCriteria ? pageTranslations.lessCriteria : pageTranslations.moreCriteria }
                        </MDBBtn>
                    </div>
                </div>
                <div className="text-center">
                    <h3 className={ isDarkMode ? "text-white" : "text-muted" }>{ pageTranslations.result }</h3>
                    <button className="btn btn-danger" onClick={ clearSearch } style={ { backgroundColor: '#f64915' } }>
                        <MDBIcon fas icon="trash-alt"/> { pageTranslations.clear }
                    </button>
                    <button className="btn btn_primary" onClick={ search }>
                        <MDBIcon fas icon="search"/> { pageTranslations.searchButton }
                    </button>
                    <div className="chips-container text-white mt-3">
                        <ChipList
                            chips={ chips }
                            setChips={ setChips }
                            textBeforeMin={ textBeforeMin }
                            textBeforeMax={ textBeforeMax }
                            refLanguageLevels={ refLanguageLevels }
                        />
                    </div>
                    {
                        ( chips.length > 0 || minYears !== 1 || maxYears !== 30 || minFormationLevel || maxFormationLevel ) ? (
                            <MDBTable>
                                <MDBTableBody>
                                    { users.length > 0 ? (
                                        users.map( ( user ) => (
                                            <React.Fragment key={ user.id }>
                                                <tr className={ isDarkMode ? "text-white" : "text-muted" }>
                                                    <td>
                                                        <div className="d-flex align-items-center"
                                                             onClick={ () => handleUserClick( user ) }
                                                             style={ { cursor: "pointer" } }>
                                                            <MDBIcon fas icon="angle-down" style={ {
                                                                transition: "transform 0.3s",
                                                                transform: dropdownStates[ user.id ] ? "rotate(180deg)" : "rotate(0deg)"
                                                            } }/>
                                                            <img
                                                                src="/spinner-alcyone.png"
                                                                alt=""
                                                                style={ { width: "45px", height: "45px" } }
                                                                className="rounded-circle"
                                                            />
                                                            <a
                                                                href={ `/user-cvs/${ user.id }` }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={ {
                                                                    cursor: "pointer",
                                                                    textDecoration: "none",
                                                                    color: "inherit"
                                                                } }
                                                            >
                                                                <p className="noLinkStyle fw-bold mb-1">
                                                                    <span className="text-uppercase">
                                                                        { user.nom }
                                                                    </span>{ " " }
                                                                    { user.prenom }
                                                                </p>
                                                            </a>
                                                        </div>
                                                    </td>
                                                    <td>
                                                    </td>
                                                    <td>
                                                    </td>
                                                    <td>
                                                    </td>
                                                </tr>
                                                { dropdownStates[ user.id ] && selectedUser && selectedUser.id === user.id && selectedResumes.length > 0 && (
                                                    selectedResumes.map( ( resume, index ) => {
                                                        const keywords = resume.properties?.keywords || [];
                                                        const resumeLanguage = resume?.properties?.documentLanguage || "fr";

                                                        return (
                                                            <tr key={ index }>
                                                                <td>
                                                                    <a
                                                                        href={ `/mon-espace-cv/${ user.id }/${ resume.id }` }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={ {
                                                                            cursor: "pointer",
                                                                            textDecoration: "none",
                                                                            color: "inherit"
                                                                        } }
                                                                    >
                                                                        <img
                                                                            src="./spinner-alcyone.png"
                                                                            alt=""
                                                                            style={ { width: "45px", height: "45px" } }
                                                                            className="rounded-circle"
                                                                        />
                                                                        <MDBBadge color="info" light>
                                                                            { generatePdfFileName( user, resumeLanguage, keywords ) }
                                                                        </MDBBadge>
                                                                    </a>
                                                                </td>
                                                                <td>
                                                                    <p className="fw-normal mb-1 text-uppercase">
                                                                        { resumeLanguage }
                                                                    </p>
                                                                </td>
                                                                <td>
                                                                    <p className="mb-0">{ keywords?.join( '_' ) || 'pas de mot-clef' }</p>
                                                                </td>
                                                                <td>
                                                                    <p className="fw-normal mb-1">
                                                                        { new Date( resume.creationDate ).toLocaleDateString(
                                                                            "fr-FR"
                                                                        ) }
                                                                    </p>
                                                                </td>
                                                            </tr>
                                                        )
                                                    } )
                                                ) }
                                            </React.Fragment>
                                        ) )
                                    ) : (
                                        <tr>
                                            <td>
                                                <p className={ isDarkMode ? "text-white" : "text-muted" }>
                                                    { pageTranslations.rechercheAvance }
                                                </p>
                                            </td>
                                        </tr>
                                    ) }
                                </MDBTableBody>
                            </MDBTable>

                        ) : (
                            <p className={ isDarkMode ? "text-white" : "text-muted" }>{ pageTranslations.rechercheAvance }</p>
                        )
                    }
                </div>
            </MDBContainer>
        </MDBContainer>
    );
}
